import { useTranslation } from "react-i18next";
import LeafRectangleLoader from "../../../common/leaf/LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import type {
  CsiBonusSchedules,
  PerfBonusScheduleTier,
} from "../../../services/CustomerSalesIncentive/CustomerSalesIncentive.types";
import defaultContent from "../../../content/csi";
import commonContent from "../../../content/common";
import "./BonusTracker.scss";

type Props = {
  bonusSchedules: CsiBonusSchedules;
  currentCSILevel: number;
  perfBonusLevel: number;
  currentGPV: number;
  isLoading: boolean;
};

const getCellClass = (itemValue: number, currentValue: number, higherIsSurpassed = false): string => {
  if (itemValue === currentValue) return "BonusTracker__cell--active";
  const surpassed = higherIsSurpassed ? itemValue > currentValue : itemValue < currentValue;
  return surpassed ? "BonusTracker__cell--surpassed" : "BonusTracker__cell--not-surpassed";
};

const getProgressBarWidth = (currentGPV: number, perfSchedule: PerfBonusScheduleTier[]): number => {
  if (currentGPV < 0 || perfSchedule.length === 0) return 0;

  const colWidth = 100 / perfSchedule.length;
  const volumes = perfSchedule.map((t) => t.Volume);

  if (currentGPV >= volumes[volumes.length - 1]) return 100;

  for (let i = 1; i < volumes.length; i++) {
    if (currentGPV <= volumes[i]) {
      const rangeStart = volumes[i - 1];
      const rangeEnd = volumes[i];
      const rangeWidth = rangeEnd - rangeStart;
      const colStart = (i - 1) * colWidth;
      if (rangeWidth === 0) return colStart;
      return colStart + ((currentGPV - rangeStart) / rangeWidth) * colWidth;
    }
  }

  return 100;
};

const BonusTracker = ({ bonusSchedules, currentCSILevel, perfBonusLevel, currentGPV, isLoading }: Props) => {
  const { t } = useTranslation(["csi", "common"]);
  const symbol = t("disclaimerSymbol", { ns: "common", defaultValue: commonContent.disclaimerSymbol });

  const csiSorted = [...bonusSchedules.CSIBonusSchedule].sort((a, b) => b - a);
  const csiSchedule = csiSorted[csiSorted.length - 1] === 0 ? csiSorted : [...csiSorted, 0];

  const perfSorted = [...bonusSchedules.PerfBonusSchedules].sort((a, b) => a.Percent - b.Percent);
  const perfSchedule = perfSorted[0]?.Percent === 0 ? perfSorted : [{ Percent: 0, Volume: 0 }, ...perfSorted];

  const progressWidth = getProgressBarWidth(currentGPV, perfSchedule);

  return (
    <div className="BonusTracker">
      <LeafRectangleLoader isLoading={isLoading} height="1.5rem" width="18rem">
        <h3 className="BonusTracker__title">{t("bonusTracker.title", defaultContent.bonusTracker.title)}</h3>
      </LeafRectangleLoader>

      <LeafRectangleLoader isLoading={isLoading} height="10rem" width="100%">
        <div className="BonusTracker__table">
          <h4 className="BonusTracker__subtitle">
            {t("bonusTracker.csiMultiplierLabel", defaultContent.bonusTracker.csiMultiplierLabel, { symbol })}
          </h4>

          {/* CSI Row */}
          <div className="BonusTracker__row">
            {csiSchedule.map((multiplier, index) => (
              <div key={`csi-${index}`} className={`BonusTracker__cell ${getCellClass(multiplier, currentCSILevel)}`}>
                {multiplier}%
              </div>
            ))}
          </div>

          {/* Progress Bar Row */}
          <div className="BonusTracker__row BonusTracker__row--progress">
            <div
              className="BonusTracker__progress-bar"
              style={{ width: `${progressWidth}%` }}
              role="progressbar"
              aria-label={t("bonusTracker.gpvLabel", defaultContent.bonusTracker.gpvLabel, {
                gpv: currentGPV.toLocaleString(),
              })}
              aria-valuenow={currentGPV}
              aria-valuemin={0}
              aria-valuemax={perfSchedule[perfSchedule.length - 1]?.Volume ?? 0}
            />
            <span
              className={`BonusTracker__progress-label${progressWidth < 15 ? " BonusTracker__progress-label--outside" : ""}`}
              style={{ left: `${progressWidth}%` }}
            >
              {t("bonusTracker.gpvLabel", defaultContent.bonusTracker.gpvLabel, {
                gpv: currentGPV.toLocaleString(),
              })}
            </span>
          </div>

          {/* Performance Bonus % Row */}
          <div className="BonusTracker__row BonusTracker__row--perf">
            {perfSchedule.map((tier, index) => (
              <div
                key={`perf-${index}`}
                className={`BonusTracker__cell ${getCellClass(tier.Percent, perfBonusLevel, false)}`}
              >
                {tier.Percent}%
              </div>
            ))}
          </div>

          {/* GPV Labels Row — labels centered on column boundaries */}
          <div className="BonusTracker__gpv-labels">
            {perfSchedule.map((tier, index) =>
              tier.Volume > 0 ? (
                <div
                  key={`gpv-label-${index}`}
                  className="BonusTracker__gpv-label"
                  style={{ left: `${(index / perfSchedule.length) * 100}%` }}
                >
                  {t("bonusTracker.gpvVolumeLabel", defaultContent.bonusTracker.gpvVolumeLabel, {
                    volume: tier.Volume.toLocaleString(),
                  })}
                </div>
              ) : null,
            )}
          </div>

          <h4 className="BonusTracker__subtitle BonusTracker__subtitle--bottom">
            {t("bonusTracker.perfBonusLevelLabel", defaultContent.bonusTracker.perfBonusLevelLabel)}
          </h4>
        </div>
      </LeafRectangleLoader>

      <LeafRectangleLoader isLoading={isLoading} height="2.5rem" width="100%">
        <p className="BonusTracker__footer">
          {t("bonusTracker.footer", defaultContent.bonusTracker.footer, { symbol })}
        </p>
      </LeafRectangleLoader>
    </div>
  );
};

export default BonusTracker;
